import { Divider } from "@chakra-ui/react";
import { useCallback } from "react";
import { FormattedMessage } from "react-intl";

import type { WorkflowProcess } from "@nens/rana/api-v1";
import { RanaHeading, useIsFeatureEnabled } from "@nens/ranalib";

import { BackButton } from "@/components/back-button";
import {
  FROM_JOB_SEARCH_KEY,
  JOB_SEARCH_KEY,
  PROCESS_SEARCH_KEY,
  WORKFLOW_SEARCH_KEY,
} from "@/config/query-parameter-consts";
import { ProcessRun } from "@/features/project-process-run";
import { WorkflowDetail, WorkflowList } from "@/features/project-workflow";
import { useRanaSearchState } from "@/hooks/router/rana-search-state";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { useProcessesTabLabel } from "@/hooks/use-processes-tab-label";
import { useWorkflowListQuery } from "@/queries/query-workflow";

import { JobList } from "./components/job-list";
import { ProcessList } from "./components/process-list";
import { useProcessOverview } from "./use-process-overview";

export function ProcessOverview() {
  const {
    hasJobOrProcess,
    setProcess,
    setProcessId,
    setJob,
    job,
    process,
    setSourceJob,
    changeCurrentJobToSourceJob,
    sourceJob,
  } = useProcessOverview();
  const { canWriteProject } = useProjectActionAllowed();
  const processesLabel = useProcessesTabLabel();
  const [workflowSlug, setWorkflowSlug] = useRanaSearchState<string | undefined>(
    WORKFLOW_SEARCH_KEY,
    undefined,
    ["string", "undefined"]
  );
  const workflowsEnabled = useIsFeatureEnabled("enableWorkflows");
  const workflowQuery = useWorkflowListQuery(workflowsEnabled);
  const selectedWorkflow = workflowQuery.data?.find(({ slug }) => slug === workflowSlug);
  const isWorkflowProcess = !!selectedWorkflow?.processes.some(({ id }) => id === process?.id);
  const showWorkflowDetail = workflowsEnabled && workflowSlug && selectedWorkflow;
  const analysisToolsLabel = (
    <FormattedMessage id="page_title.project.analysis_tools" defaultMessage="Analysis tools" />
  );

  const selectStandaloneProcess = (selectedProcess: NonNullable<typeof process>) => {
    setProcess(selectedProcess, { clearOthers: [WORKFLOW_SEARCH_KEY] });
  };

  const selectWorkflowProcess = (workflowProcess: WorkflowProcess) => {
    setProcessId(workflowProcess.id);
  };

  const clearWorkflow = useCallback(() => {
    setWorkflowSlug(undefined);
    setProcess(undefined);
    setJob(undefined);
  }, [setWorkflowSlug, setProcess, setJob]);

  const clearProcess = useCallback(() => {
    setProcess(undefined);
    setJob(undefined);
  }, [setProcess, setJob]);

  if (showWorkflowDetail)
    return (
      <WorkflowDetail
        workflow={selectedWorkflow}
        onBack={clearWorkflow}
        onSelectProcess={selectWorkflowProcess}
        showProcessCards={!hasJobOrProcess}
        selectedProcessId={process?.id}
      >
        {hasJobOrProcess && (
          <>
            <BackButton mt={4} back={clearProcess}>
              <FormattedMessage id="workflow.back_to_workflow" defaultMessage="Back to workflow" />
            </BackButton>
            <ProcessRun
              key={process?.id}
              sourceJob={sourceJob}
              changeCurrentJobToSourceJob={changeCurrentJobToSourceJob}
              setJob={setJob}
              job={job}
              process={process}
              workflowId={isWorkflowProcess ? selectedWorkflow.id : undefined}
            />
          </>
        )}
      </WorkflowDetail>
    );

  if (hasJobOrProcess)
    return (
      <>
        <BackButton back={clearProcess}>{processesLabel}</BackButton>
        <ProcessRun
          key={process?.id}
          sourceJob={sourceJob}
          changeCurrentJobToSourceJob={changeCurrentJobToSourceJob}
          setJob={setJob}
          job={job}
          process={process}
          workflowId={isWorkflowProcess ? selectedWorkflow?.id : undefined}
        />
      </>
    );

  return (
    <>
      {workflowsEnabled &&
        (workflowSlug && selectedWorkflow ? null : (
          <>
            <WorkflowList
              onSelect={(workflow) =>
                setWorkflowSlug(workflow.slug, {
                  clearOthers: [FROM_JOB_SEARCH_KEY, JOB_SEARCH_KEY, PROCESS_SEARCH_KEY],
                })
              }
            />
            <Divider my={6} />
          </>
        ))}
      {workflowsEnabled && !workflowSlug && (
        <RanaHeading mt={4} mb={4} type="h4">
          {analysisToolsLabel}
        </RanaHeading>
      )}
      {!workflowSlug && canWriteProject && <ProcessList setProcess={selectStandaloneProcess} />}
      <JobList setSourceJob={setSourceJob} setJob={setJob} />
    </>
  );
}
